import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { EnumSignerRoleType, EnumSignTagTypes } from "@modals/app.modal";

export type SignToolbarAction = {
  kind: "signature" | "comment" | "tag";
  type?: EnumSignTagTypes;
  dimensions?: any;
};


export type SignToolbarListItem = {
  kind: "signature" | "comment" | "tag";
  type?: EnumSignTagTypes;
};

const SIGNER_ACTION_TOKENS = new Set([
  "SIGNATURE",
  "COMMENT",
  "STAMP",
  "CHECKBOX",
  "DATE",
  "INITIALS",
  "TEXT",
]);

type ToolbarActionFilterMode = "all" | "none" | "subset";

function isSignerRole(role: string | null | undefined): boolean {
  return (role || "").toLowerCase() === EnumSignerRoleType.SIGNER.toLowerCase();
}

function enumTagToSignerActionToken(type: EnumSignTagTypes): string {
  switch (type) {
    case EnumSignTagTypes.INITIALS:
      return "INITIALS";
    case EnumSignTagTypes.TEXT:
      return "TEXT";
    case EnumSignTagTypes.DATE:
      return "DATE";
    case EnumSignTagTypes.CHECKBOX:
      return "CHECKBOX";
    case EnumSignTagTypes.STAMP:
      return "STAMP";
    case EnumSignTagTypes.COMMENT:
      return "COMMENT";
    case EnumSignTagTypes.SIGNATURE:
      return "SIGNATURE";
    default:
      return "";
  }
}

@Injectable({
  providedIn: "root",
})
export class SignToolbarService {
  private _active$ = new BehaviorSubject<boolean>(false);
  private _expanded$ = new BehaviorSubject<boolean>(false);
  private _signerRole$ = new BehaviorSubject<EnumSignerRoleType | null>(null);
  private _action$ = new Subject<SignToolbarAction>();

  private _signerActionsRaw: string | null | undefined = undefined;
  private _filterMode: ToolbarActionFilterMode = "all";
  private _allowedSignerActions = new Set<string>();

  get active$() {
    return this._active$.asObservable();
  }

  get expanded$() {
    return this._expanded$.asObservable();
  }

  get signerRole$() {
    return this._signerRole$.asObservable();
  }

  get action$() {
    return this._action$.asObservable();
  }

  setActive(value: boolean): void {
    this._active$.next(value);
  }

  setExpanded(value: boolean): void {
    this._expanded$.next(value);
  }

  toggleExpanded(): void {
    this._expanded$.next(!this._expanded$.value);
  }

  setSignerRole(role: EnumSignerRoleType | null): void {
    this._signerRole$.next(role);
    this._recomputeSignerActionFilter();
  }

  /**
   * DownloadSign `SignerActions` (only enforced when role is Signer).
   * null / undefined / "" → all tools; "All" → all; "TaggedSignatureOnly" → hide toolbar tools;
   * comma-separated SIGNATURE, COMMENT, … → only those buttons.
   */
  setSignerActions(signerActions: string | null | undefined): void {
    this._signerActionsRaw = signerActions;
    this._recomputeSignerActionFilter();
  }

  /** Call from sign screen teardown so the next page does not inherit CSV rules. */
  clearSignerActions(): void {
    this._signerActionsRaw = undefined;
    this._filterMode = "all";
    this._allowedSignerActions = new Set();
  }

  /**
   * Whether a toolbar row should be rendered for the current signer policy.
   * Non-signer roles always see the full toolbar (existing disable rules still apply per item).
   */
  isToolbarItemVisible(item: SignToolbarListItem): boolean {
    const role = this._signerRole$.value;
    if (!isSignerRole(role as string | undefined)) {
      return true;
    }
    if (this._filterMode === "all") {
      return true;
    }
    if (this._filterMode === "none") {
      return false;
    }
    if (item.kind === "signature") {
      return this._allowedSignerActions.has("SIGNATURE");
    }
    if (item.kind === "comment") {
      return this._allowedSignerActions.has("COMMENT");
    }
    if (item.kind === "tag" && item.type != null) {
      const token = enumTagToSignerActionToken(item.type);
      return Boolean(token) && this._allowedSignerActions.has(token);
    }
    return false;
  }

  private _recomputeSignerActionFilter(): void {
    const role = this._signerRole$.value;
    if (!isSignerRole(role as string | undefined)) {
      this._filterMode = "all";
      this._allowedSignerActions = new Set();
      return;
    }

    const raw = this._signerActionsRaw;
    if (raw == null || (typeof raw === "string" && raw.trim() === "")) {
      this._filterMode = "all";
      this._allowedSignerActions = new Set();
      return;
    }

    const trimmed = String(raw).trim();
    const lower = trimmed.toLowerCase();
    if (lower === "all") {
      this._filterMode = "all";
      this._allowedSignerActions = new Set();
      return;
    }
    if (lower === "taggedsignatureonly") {
      this._filterMode = "none";
      this._allowedSignerActions = new Set();
      return;
    }

    const parts = trimmed
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    const allowed = new Set(parts.filter((p) => SIGNER_ACTION_TOKENS.has(p)));
    this._allowedSignerActions = allowed;
    if (parts.length === 0) {
      this._filterMode = "all";
    } else {
      this._filterMode = allowed.size > 0 ? "subset" : "none";
    }
  }

  emit(action: SignToolbarAction): void {
    this._action$.next(action);
  }
}
